import React, { createRef, Suspense, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { initializeAuthInterceptor } from '@groma-dev/scaffold';
import { BypassBlock, classNames, getTestIdAttribute, ToastProvider } from '@groma-dev/blueprint';
import { Route, Routes, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';
import { AppConfig } from '../../../core/AppConfig';
import { jumpToAnchorRef, scrollToTop, isAuthenticatedRoute } from '../../utils/WebHelper';
import { useDataStore } from '../../providers/StoresProvider';
import { ComponentFactory } from '../../ComponentFactory';
import { AppRoutes } from '../../../core/routes';
import { APIClient } from '../../../core/apiclient/APIClient';
import { InvitationMode } from '../../pages/invitations/Invitations';
import AuthRouter from '../authrouter/AuthRouter';
import { verificationRoutes } from '../../../core/routes/AppRoutes';

import { getAppStyles } from './styles/App.styles';

const ErrorPage = React.lazy(() => ComponentFactory.ErrorPageAsync);
const Home = React.lazy(() => ComponentFactory.HomeAsync);
const Invitations = React.lazy(() => ComponentFactory.InvitationsAsync);
const MultiFactorAuth = React.lazy(() => ComponentFactory.MultiFactorVerificationAsync);
const NavBar = React.lazy(() => ComponentFactory.NavBarAsync);
const Profile = React.lazy(() => ComponentFactory.ProfileAsync);
const PhoneVerification = React.lazy(() => ComponentFactory.PhoneVerificationAsync);
const ResetPassword = React.lazy(() => ComponentFactory.ResetPasswordAsync);
const Security = React.lazy(() => ComponentFactory.SecurityAsync);
const SignIn = React.lazy(() => ComponentFactory.SignInAsync);

let authRedirectInitialized = false;

export interface AppProps {}

const AppContent = observer(() => {
  const styles = getAppStyles();
  const dataStore = useDataStore();
  const { pathname } = useLocation();
  const { userState } = dataStore;
  const showWelcomeComponent = isAuthenticatedRoute(pathname) && !Object.values(verificationRoutes).includes(pathname);
  // Routes returned here should be Authenticated Routes Only
  return (
    <div className={classNames(styles.appWrapper)}>
      <ComponentFactory.Header />
      <div id="mainContent" className={classNames(styles.mainContainer)}>
        <Suspense fallback={<ComponentFactory.Loading isPage />}>
          {showWelcomeComponent && (
            <>
              <div className={classNames(styles.intro)}>
                <h1 className={classNames(styles.introTitle)} {...getTestIdAttribute('welcomeTitle')}>
                  <FormattedMessage
                    id="app.welcome.title"
                    defaultMessage="Welcome, {firstName}"
                    description="Welcome title content addressing user"
                    values={{
                      firstName:
                        userState.user?.first_name.length !== 0 ? userState.user?.first_name : userState.user.email,
                    }}
                  />
                </h1>
                <p className={classNames(styles.introMessage)}>
                  <FormattedMessage
                    id="app.welcome.message"
                    defaultMessage="Manage personal information and security settings associated with your Groma account."
                    description="Welcome helper message content"
                  />
                </p>
              </div>
              <NavBar />
            </>
          )}
          <div className={classNames(styles.mainWrapper)}>
            <ComponentFactory.ErrorBoundary>
              <Routes>
                <Route path={AppRoutes.root} element={<Home />} />
                <Route path={AppRoutes.PhoneVerification} element={<PhoneVerification />} />
                <Route path={AppRoutes.Profile} element={<Profile />} />
                <Route path={AppRoutes.Security} element={<Security />} />
                {/* <Route path={AppRoutes.Invitations} element={<Invitations />}> */}
                {/*  <Route index element={<Invitations />} /> */}
                {/*  /!* // Invitation Routes to go here *!/ */}
                {/* </Route> */}
                <Route path="*" element={<ErrorPage />} />
              </Routes>
            </ComponentFactory.ErrorBoundary>
          </div>
        </Suspense>
      </div>
    </div>
  );
});

const App = observer(() => {
  const styles = getAppStyles();
  const dataStore = useDataStore();
  const { pathname, hash } = useLocation();
  const { userState } = dataStore;
  const { Authentication, Domains } = AppConfig.Settings;
  const { name: authCookieName, domain } = Authentication.cookie;
  const resetRef = createRef<HTMLSpanElement>();

  if (Cookies.get(authCookieName) && !authRedirectInitialized) {
    initializeAuthInterceptor(APIClient, `${Domains.id.baseUrl}${AppRoutes.SignIn}`, {
      name: authCookieName,
      domain,
    });
    authRedirectInitialized = true;
  }

  useEffect(() => {
    if (hash) jumpToAnchorRef();
    else {
      scrollToTop();
      resetRef.current?.focus();
    }
  }, [pathname]);

  // Routes returned here are either Non-Authenticated Routes
  // or Authenticated Routes that want the header hidden
  return (
    <>
      <span ref={resetRef} tabIndex={-1} />
      {userState.user && (
        <BypassBlock id="mainContent">
          <FormattedMessage
            id="common.bypassBlocks.mainContent"
            defaultMessage="Skip to Main Content"
            description="Main bypass block content"
          />
        </BypassBlock>
      )}
      <ToastProvider>
        <div className={classNames(styles.appContainer)}>
          <AuthRouter>
            <ComponentFactory.ErrorBoundary>
              <Suspense>
                <Routes>
                  <Route path={AppRoutes.SignIn} element={<SignIn />} />
                  <Route path={AppRoutes.ResetPassword} element={<ResetPassword />} />
                  <Route path={AppRoutes.AcceptInvitation} element={<Invitations mode={InvitationMode.ACCEPT} />} />
                  <Route path={AppRoutes.MultiFactorAuth} element={<MultiFactorAuth />} />
                  <Route path="*" element={<AppContent />} />
                </Routes>
                <ComponentFactory.Footer />
              </Suspense>
            </ComponentFactory.ErrorBoundary>
          </AuthRouter>
        </div>
      </ToastProvider>
    </>
  );
});

export default App;
